import {DataTable} from "../DataTable/DataTable";
import {AllocatableLoadEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Group} from "@mantine/core";
import {IconCircleCheckFilled, IconEye} from "@tabler/icons-react";
import {useNavigate} from "react-router-dom";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity"
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";

const EMPTY_ARRAY: AllocatableLoadEntity[] = []

export type AllocatableLoadDataTableProps = EntityDataTableProps<AllocatableLoadEntity>

export const AllocatableLoadDataTable = (props: AllocatableLoadDataTableProps) => {
  const {
    records: loads = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: loads,
    sortStatus: {columnAccessor: 'dttm', direction: 'desc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("common.entity.allocatableload.table.column.dttm", "Date"),
          accessor: "dttm",
          width: 100,
          sortable: true,
          render: ({dttm}) => (<FormattedDateTime date={dttm}/>)
        },
        {
          title: t("common.entity.allocatableload.table.column.site", "Site"),
          accessor: "category",
          width: 100,
          sortable: true,
          render: ({siteName}) => (<DefaultText value={siteName}/>),
        },
        {
          title: t("common.entity.allocatableload.table.column.container", "Container"),
          accessor: "containerName",
          width: 100,
          sortable: true,
          render: ({containerName}) => (<DefaultText value={containerName}/>),
        },
        {
          title: t("common.entity.allocatableload.table.column.volume", "Volume"),
          accessor: "volume",
          width: 100,
          sortable: true,
          render: ({volume, volumeUnitOfMeasure}) => (
            <FormattedQuantity
              value={{
                number: volume,
                units: volumeUnitOfMeasure
              }}/>
          )
        },
        {
          title: t("common.entity.allocatableload.table.column.type", "Type"),
          accessor: "cargoSubtype",
          width: 100,
          sortable: true,
          render: ({cargoSubtype}) => (
            <>
              {t(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage)}
            </>
          )
        },
        {
          title: t("common.entity.allocatableload.table.column.status", "Status"),
          accessor: "resolved",
          width: 100,
          sortable: true,
          render: ({resolved}) => (resolved ? <IconCircleCheckFilled size={16}/> : undefined)
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: ({id}) => (
            <Group spacing={4} position="right" noWrap>
              <ActionIcon onClick={() => navigate(`/allocatable-loads/profile?id=${id}`)}>
                <IconEye size={16}/>
              </ActionIcon>
            </Group>
          )
        }
      ]}
    />
  );
};
